import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Disclaimer from "@/components/Disclaimer";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { MoodGlyph, EnergyGlyph, SleepGlyph, StressGlyph } from "@/components/CustomIcons";

const SCALES = [
  { key: "mood", label: "How is your mood?", icon: MoodGlyph, low: "Low", high: "Bright" },
  { key: "energy", label: "How is your energy?", icon: EnergyGlyph, low: "Drained", high: "Energized" },
  { key: "sleep", label: "How did you sleep?", icon: SleepGlyph, low: "Restless", high: "Deep & restful" },
];

const REFLECT_QS = [
  { key: "connections", q: "Did you connect well with loved ones or coworkers today?" },
  { key: "impatient", q: "Did you feel impatient or snappy?" },
  { key: "off_plan", q: "Did you eat something not on your plan?" },
];

export default function Ritual() {
  const { user } = useAuth();
  const firstName = user?.name?.split(" ")[0] || "friend";
  const [values, setValues] = useState({ mood: 3, energy: 3, sleep: 3, stress: 3 });
  const [symptoms, setSymptoms] = useState([]);
  const [selected, setSelected] = useState([]);
  const [reflection, setReflection] = useState("");
  const [reflections, setReflections] = useState({});
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedScore, setSavedScore] = useState(null);
  const [showNudge, setShowNudge] = useState(false);

  const load = () => {
    api.get("/symptoms").then((r) => setSymptoms(r.data)).catch(() => {});
    api.get("/ritual/history?days=30").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/ritual/today").then((r) => {
      const t = r.data;
      if (t && t.wellness_score != null) {
        setValues({ mood: t.mood, energy: t.energy, sleep: t.sleep, stress: t.stress });
        setSelected(t.symptoms || []);
        setReflection(t.reflection || "");
        setReflections(t.reflections || {});
        setSavedScore(t.wellness_score);
      }
    }).catch(() => {});
  };
  useEffect(load, []);

  const liveScore = Math.round(((values.mood + values.energy + values.sleep + (6 - values.stress)) / 20) * 100);

  const toggleSymptom = (s) =>
    setSelected((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/ritual", { ...values, symptoms: selected, reflection, reflections });
      setSavedScore(data.wellness_score);
      toast.success("Your ritual is saved. Be gentle with yourself today.");
      setShowNudge(true);
      const h = await api.get("/ritual/history?days=30");
      setHistory(h.data);
    } catch {
      toast.error("Could not save your ritual");
    } finally {
      setSaving(false);
    }
  };

  const chartData = history.map((h) => ({ date: h.date.slice(5), score: h.wellness_score }));

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <div className="text-center animate-fade-up">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Your daily ritual</span>
          <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">Check in with yourself, {firstName}</h1>
          <p className="mt-3 text-base text-[#6E5862]">A gentle moment of self-tracking. There are no wrong answers.</p>
        </div>

        {/* Score circle */}
        <div className="mt-10 flex justify-center">
          <div className="relative h-44 w-44">
            <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="44" fill="none" stroke="#F6ECF0" strokeWidth="8" />
              <circle cx="50" cy="50" r="44" fill="none" stroke="#6B2554" strokeWidth="8" strokeLinecap="round"
                strokeDasharray={`${(liveScore / 100) * 276.5} 276.5`} className="transition-all duration-500" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span data-testid="live-score" className="font-serif text-5xl text-wine">{liveScore}</span>
              <span className="text-xs uppercase tracking-[0.15em] text-[#9B7E8D]">Wellness</span>
            </div>
          </div>
        </div>

        {/* Scales */}
        <div className="mt-10 space-y-8">
          {SCALES.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.key} className="bg-white rounded-3xl p-6 border border-taupe">
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-11 w-11 rounded-2xl bg-wine/10 flex items-center justify-center text-wine">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="font-serif text-2xl text-[#3A1230]">{s.label}</h3>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      data-testid={`ritual-${s.key}-${n}`}
                      onClick={() => setValues({ ...values, [s.key]: n })}
                      className={`flex-1 h-12 rounded-2xl text-sm font-medium transition-all ${
                        values[s.key] === n
                          ? "bg-wine text-white shadow-[0_8px_20px_rgba(107,37,84,0.2)]"
                          : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-[#9B7E8D]">
                  <span>{s.low}</span><span>{s.high}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Symptoms */}
        <div className="mt-8 bg-white rounded-3xl p-6 border border-taupe">
          <h3 className="font-serif text-2xl text-[#3A1230] mb-1">Anything you're noticing?</h3>
          <p className="text-sm text-[#9B7E8D] mb-4">Optional — tap any that apply today.</p>
          <div className="flex flex-wrap gap-2.5">
            {symptoms.map((s) => (
              <button
                key={s}
                data-testid={`symptom-${s.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => toggleSymptom(s)}
                className={`rounded-full px-4 py-2 text-sm transition-all ${
                  selected.includes(s)
                    ? "bg-wine text-white"
                    : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Daily reflections */}
        <div className="mt-8 bg-white rounded-3xl p-6 border border-taupe">
          <h3 className="font-serif text-2xl text-[#3A1230] mb-1">Your daily moods</h3>
          <p className="text-sm text-[#9B7E8D] mb-5">A few gentle questions — there's no judgment here.</p>
          <div className="space-y-5">
            {REFLECT_QS.map((item) => (
              <div key={item.key} data-testid={`reflect-q-${item.key}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <span className="text-base text-[#3A1230]">{item.q}</span>
                <div className="flex gap-2 flex-shrink-0">
                  {["yes", "no"].map((ans) => (
                    <button
                      key={ans}
                      data-testid={`reflect-${item.key}-${ans}`}
                      onClick={() => setReflections((prev) => ({ ...prev, [item.key]: ans }))}
                      className={`rounded-full px-5 py-2 text-sm capitalize transition-all ${
                        reflections[item.key] === ans
                          ? "bg-wine text-white"
                          : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"
                      }`}
                    >
                      {ans}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reflection */}
        <div className="mt-8 bg-white rounded-3xl p-6 border border-taupe">
          <h3 className="font-serif text-2xl text-[#3A1230] mb-4">A note to yourself</h3>
          <textarea
            data-testid="ritual-reflection"
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            rows={4}
            placeholder="What's on your heart this morning?"
            className="w-full rounded-2xl border border-[#D8C2CD] bg-cream px-4 py-3 text-base text-[#3A1230] placeholder-[#9B7E8D] focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine resize-none"
          />
        </div>

        <button
          data-testid="ritual-save-btn"
          onClick={save}
          disabled={saving}
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-wine px-8 py-4 text-sm font-medium text-white transition-all hover:bg-wine-dark disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-5 w-5" /> {savedScore != null ? "Update today's ritual" : "Complete ritual"}</>}
        </button>

        {/* After-ritual nudge */}
        {showNudge && (
          <div data-testid="ritual-nudge" className="mt-6 relative overflow-hidden rounded-3xl bg-wine p-6 text-white animate-fade-up">
            <button data-testid="ritual-nudge-dismiss" onClick={() => setShowNudge(false)} className="absolute top-4 right-4 text-white/60 hover:text-white text-sm">Dismiss</button>
            <p className="font-serif text-2xl">Want to talk through how you're feeling?</p>
            <p className="mt-1 text-sm text-white/80 max-w-md">Your BloomHer AI companion can offer gentle, non-clinical encouragement based on today's check-in.</p>
            <Link
              to={`/assistant?q=${encodeURIComponent("Based on how I'm feeling today, can you share some gentle wellness encouragement and ideas?")}`}
              data-testid="ritual-nudge-cta"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-wine hover:bg-cream transition-all"
            >
              Ask BloomHer about today
            </Link>
          </div>
        )}

        {/* Trend */}
        {chartData.length > 1 && (
          <div className="mt-12 bg-white rounded-3xl p-6 border border-taupe">
            <h3 className="font-serif text-2xl text-[#3A1230] mb-4">Your wellness trend</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBD9E1" vertical={false} />
                  <XAxis dataKey="date" stroke="#9B7E8D" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 100]} stroke="#9B7E8D" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EBD9E1", fontFamily: "Jost" }} />
                  <Line type="monotone" dataKey="score" stroke="#6B2554" strokeWidth={2.5} dot={{ fill: "#6B2554", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <Disclaimer className="mt-10 text-center" />
      </div>
    </AppLayout>
  );
}
